import React, { useState } from "react";
import { getCurrentPosition, getGeolocationErrorMessage } from "../../utils/geolocation";

function portalStatus(client) {
  if (!client.portalEnabled) return { label: "Portal Off", cls: "bg-gray-100 text-gray-700" };
  if (!client.portalAuthUid) return { label: "Not Invited", cls: "bg-amber-100 text-amber-800" };
  if (client.portalAccessStatus === "disabled") return { label: "Disabled", cls: "bg-gray-200 text-gray-700" };
  if (client.portalAccessStatus === "active") return { label: "Active", cls: "bg-emerald-100 text-emerald-800" };
  return { label: "Invited", cls: "bg-blue-100 text-blue-800" };
}

export default function ClientFormModal({
  title,
  client,
  errors,
  isSaving,
  portalBusy,
  onChange,
  onSave,
  onClose,
  onSendInvite,
  onResetPortal,
  onTogglePortal,
  limitedMode = false,
}) {
  const [capturing, setCapturing] = useState(false);
  const [geoMessage, setGeoMessage] = useState("");
  const update = (field, value) => onChange({ ...client, [field]: value });
  const contacts = Array.isArray(client.additionalContacts) ? client.additionalContacts : [];
  const status = portalStatus(client);
  const hasPortalUser = Boolean(client.portalAuthUid);
  const portalActive = hasPortalUser && client.portalAccessStatus !== "disabled";

  const updateContact = (index, field, value) => {
    const next = contacts.map((item, i) => i === index ? { ...item, [field]: value } : item);
    if (field === "isPrimary" && value) next.forEach((item, i) => { if (i !== index) item.isPrimary = false; });
    update("additionalContacts", next);
  };

  const addContact = () => update("additionalContacts", [...contacts, { name: "", mobile: "", isPrimary: contacts.length === 0 }]);
  const removeContact = (index) => update("additionalContacts", contacts.filter((_, i) => i !== index));

  const captureLocation = async () => {
    setCapturing(true);
    setGeoMessage("");
    try {
      const position = await getCurrentPosition();
      onChange({
        ...client,
        latitude: Number(position.coords.latitude.toFixed(7)),
        longitude: Number(position.coords.longitude.toFixed(7)),
      });
      setGeoMessage(`✅ Coordinates captured (accuracy ±${Math.round(position.coords.accuracy)} metres).`);
    } catch (error) {
      setGeoMessage(getGeolocationErrorMessage(error));
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="text-2xl text-gray-500">×</button>
        </div>

        <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
          {limitedMode && <div className="md:col-span-2 rounded-lg border bg-slate-50 p-3 text-sm text-slate-700">Accounts access is limited to PAN/GSTIN, contact details, address and additional contacts for <strong>{client.name}</strong>.</div>}
          {!limitedMode && <Field label="Client Name *" error={errors.name}><input className="input" value={client.name} onChange={(e) => update("name", e.target.value)} /></Field>}
          <Field label="PAN *" error={errors.pan}><input className="input uppercase" value={client.pan} onChange={(e) => update("pan", e.target.value.toUpperCase())} /></Field>
          <Field label="GSTIN" error={errors.gstin}><input className="input uppercase" value={client.gstin} onChange={(e) => update("gstin", e.target.value.toUpperCase())} /></Field>
          <Field label="Contact Person"><input className="input" value={client.contactPerson} onChange={(e) => update("contactPerson", e.target.value)} /></Field>
          <Field label="Mobile" error={errors.mobile}><input className="input" value={client.mobile} onChange={(e) => update("mobile", e.target.value)} /></Field>
          <Field label="Email" error={errors.email}><input type="email" className="input" value={client.email} onChange={(e) => update("email", e.target.value)} /></Field>

          <div className="md:col-span-2 rounded-lg border bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div><h3 className="font-semibold text-gray-900">Additional WhatsApp Contacts</h3><p className="text-xs text-gray-500">Optional named mobile numbers. The existing Mobile field remains unchanged.</p></div>
              <button type="button" onClick={addContact} className="rounded border border-blue-600 px-3 py-1.5 text-sm font-semibold text-blue-700">+ Add Contact</button>
            </div>
            {!contacts.length ? <p className="text-sm text-gray-500">No additional contacts added.</p> : <div className="space-y-3">{contacts.map((item,index)=><div key={index} className="grid gap-2 rounded-lg bg-slate-50 p-3 md:grid-cols-[1fr_1fr_auto_auto] md:items-end"><Field label="Name"><input className="input" value={item.name||""} onChange={(e)=>updateContact(index,"name",e.target.value)} /></Field><Field label="Mobile" error={errors[`additionalContact_${index}`]}><input className="input" value={item.mobile||""} onChange={(e)=>updateContact(index,"mobile",e.target.value)} /></Field><label className="flex items-center gap-2 pb-2 text-sm"><input type="checkbox" checked={Boolean(item.isPrimary)} onChange={(e)=>updateContact(index,"isPrimary",e.target.checked)} /> Primary</label><button type="button" onClick={()=>removeContact(index)} className="mb-1 rounded border border-red-200 px-3 py-2 text-sm text-red-700">Remove</button></div>)}</div>}
          </div>

          {!limitedMode && <div className="md:col-span-2 rounded-lg border border-violet-200 bg-violet-50/40 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-gray-900">Client Portal Access</h3>
                <p className="text-xs text-gray-500">Enable portal, save the login email, then send the secure invite. Access Status is system-controlled.</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${status.cls}`}>{status.label}</span>
            </div>

            <div className="mt-3 grid gap-4 md:grid-cols-3">
              <Field label="Portal Enabled">
                <select className="input" value={client.portalEnabled ? "yes" : "no"} onChange={(e)=>update("portalEnabled",e.target.value==="yes")} disabled={hasPortalUser}>
                  <option value="no">Disabled</option>
                  <option value="yes">Enabled</option>
                </select>
              </Field>
              <Field label="Login Email" error={errors.portalLoginEmail}>
                <input type="email" className="input" value={client.portalLoginEmail||""} onChange={(e)=>update("portalLoginEmail",e.target.value)} disabled={!client.portalEnabled || hasPortalUser} />
              </Field>
              <Field label="Access Status">
                <div className="flex min-h-[42px] items-center rounded-md border border-gray-300 bg-gray-100 px-3 text-sm font-medium text-gray-700">{status.label}</div>
              </Field>
            </div>

            {client.id && client.portalEnabled && !hasPortalUser && (
              <div className="mt-4">
                <button type="button" disabled={portalBusy || isSaving || !client.portalLoginEmail} onClick={onSendInvite} className="rounded bg-[#0B1F33] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {portalBusy ? "Sending Invite…" : "Send Invite"}
                </button>
                <p className="mt-2 text-xs text-gray-500">This saves the current client details first, creates the secure client login, and sends a password-setup email.</p>
              </div>
            )}

            {!client.id && client.portalEnabled && (
              <p className="mt-4 text-xs text-amber-800">Save this new client first. The Send Invite button will then become available when you edit the client.</p>
            )}

            {hasPortalUser && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" disabled={portalBusy} onClick={onResetPortal} className="rounded border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-700 disabled:opacity-50">Send Password Reset</button>
                <button type="button" disabled={portalBusy} onClick={() => onTogglePortal(!portalActive)} className={`rounded px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${portalActive ? "bg-red-600" : "bg-emerald-600"}`}>{portalActive ? "Disable Login" : "Enable Login"}</button>
              </div>
            )}
          </div>}

          <Field label="Address 1"><input className="input" value={client.address1} onChange={(e) => update("address1", e.target.value)} /></Field>
          <Field label="Address 2"><input className="input" value={client.address2} onChange={(e) => update("address2", e.target.value)} /></Field>
          <Field label="City"><input className="input" value={client.city} onChange={(e) => update("city", e.target.value)} /></Field>
          <Field label="State"><input className="input" value={client.state} onChange={(e) => update("state", e.target.value)} /></Field>
          <Field label="PIN" error={errors.pinCode}><input className="input" value={client.pinCode} onChange={(e) => update("pinCode", e.target.value)} /></Field>
          {!limitedMode && <Field label="Status"><select className="input" value={client.status} onChange={(e) => update("status", e.target.value)}><option value="Active">Active</option><option value="Inactive">Inactive</option></select></Field>}

          {!limitedMode && <div className="md:col-span-2 rounded-lg border bg-slate-50 p-4">
            <div className="mb-3"><h3 className="font-semibold text-gray-900">Attendance Location Coordinates</h3><p className="text-xs text-gray-500">Optional. When both coordinates are saved, client-visit attendance will require geo-validation.</p></div>
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Latitude" error={errors.coordinates}><input type="number" step="any" className="input" value={client.latitude ?? ""} onChange={(e) => update("latitude", e.target.value)} /></Field>
              <Field label="Longitude"><input type="number" step="any" className="input" value={client.longitude ?? ""} onChange={(e) => update("longitude", e.target.value)} /></Field>
              <Field label="Allowed Radius (metres)" error={errors.geoRadiusMeters}><input type="number" min="50" max="2000" step="50" className="input" value={client.geoRadiusMeters ?? ""} onChange={(e) => update("geoRadiusMeters", e.target.value)} placeholder="Use default" /></Field>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3"><button type="button" onClick={captureLocation} disabled={capturing} className="rounded border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-700 disabled:opacity-50">{capturing ? "Detecting…" : "Capture Current Location"}</button><button type="button" onClick={() => onChange({ ...client, latitude: "", longitude: "", geoRadiusMeters: "" })} className="text-sm font-medium text-gray-600">Clear coordinates</button>{geoMessage && <span className={`text-sm ${geoMessage.startsWith("✅") ? "text-green-700" : "text-red-700"}`}>{geoMessage}</span>}</div>
          </div>}

          {!limitedMode && <div className="md:col-span-2"><Field label="Remarks"><textarea rows="3" className="input" value={client.remarks} onChange={(e) => update("remarks", e.target.value)} /></Field></div>}
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-md border px-4 py-2 hover:bg-gray-50">Cancel</button>
          <button type="button" disabled={isSaving || portalBusy} onClick={onSave} className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60">{isSaving ? "Saving..." : "Save"}</button>
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #d1d5db;border-radius:.375rem;padding:.5rem .75rem;outline:none;background:#fff;color:#111827}.input:focus{border-color:#3b82f6}`}</style>
    </div>
  );
}

function Field({ label, error, children }) { return <label className="block"><span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>{children}{error && <span className="mt-1 block text-xs text-red-600">{error}</span>}</label>; }
